import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { WindowReferenceService } from '../../../services/window-reference/window-reference.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';

/**
 * @group Data Visualization
 * @component Progress Bar
 *
 * @export
 * @class LsqProgressBarComponent
 * @property {number} value Amount of progress to be shown between 0 and 100
 * @property {string} headLabel Label to be shown on the left of the bar
 * @property {string} tailLabel Label to be shown above the actual progress
 */
@LifecycleAware()
@Component({
    selector: 'lsq-progress-bar',
    template: `
        <div class="progress-bar-component" #progressBarElement>
            <div *ngIf="headLabel || tailLabel" class="label-wrapper">
                <div *ngIf="!!headLabel" class="head-label">{{ headLabel }}</div>
                <div
                    *ngIf="!!tailLabel"
                    #tailLabelElement
                    [style.left]="tailLabelPosition + 'px'"
                    class="tail-label"
                >
                    {{ tailLabel }}
                </div>
            </div>
            <div class="progress-wrapper" [ngClass]="{ 'progress-wrapper-skinny': skinny }">
                <div class="progress-bar progress-bar-bg"></div>
                <div
                    [style.width.px]="fillWidth"
                    class="progress-bar progress-bar-value"
                    [ngClass]="progressBarValueClassList"
                ></div>
            </div>
        </div>
    `,
    styleUrls: ['./lsq-progress-bar.component.scss']
})
export class LsqProgressBarComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('tailLabelElement') public tailLabelElement: ElementRef;
    @ViewChild('progressBarElement') public progressBarElement: ElementRef;
    @Input() public value = 0;
    @Input() public headLabel?: string;
    @Input() public tailLabel?: string;
    @Input() public dangerAboveThreshold?: number;
    @Input() public dangerBelowThreshold?: number;
    @Input() public danger = false;
    @Input() public skinny = false;

    public tailLabelPosition = 0;
    public fillWidth = 0;
    public progressBarValueClassList = [];

    constructor(private _windowReferenceService: WindowReferenceService, private _ngZone: NgZone) {}

    public ngAfterViewInit(): void {
        this._windowReferenceService.resizeStream.pipe(forLifeOf(this)).subscribe(() => {
            this.updateTailLabelPosition();
            this.updateFillWidth();
        });
    }

    public ngOnChanges(): void {
        this.updateTailLabelPosition();
        this.updateFillWidth();
        this.updateClassList();
    }

    public ngOnDestroy(): void {}

    public getProgressWidth(): number {
        if (!this.progressBarElement) {
            return 0;
        }
        return (
            this.progressBarElement.nativeElement.getBoundingClientRect().width *
            Math.min(this.value / 100, 1)
        );
    }

    public updateTailLabelPosition(): void {
        if (!this.tailLabelElement) {
            return;
        }

        Utilities.executeAfterRender(() =>
            this._ngZone.run(() => {
                this.tailLabelPosition = Math.max(
                    0,
                    this.getProgressWidth() -
                        this.tailLabelElement.nativeElement.getBoundingClientRect().width
                );
            })
        );
    }

    public updateFillWidth(): void {
        Utilities.executeAfterRender(() =>
            this._ngZone.run(() => {
                this.fillWidth = this.getProgressWidth();
            })
        );
    }

    public updateClassList(): void {
        Utilities.executeAfterRender(() =>
            this._ngZone.run(() => {
                this.progressBarValueClassList = [];
                if (this._isDanger()) {
                    this.progressBarValueClassList.push('danger');
                }
            })
        );
    }

    private _isDanger(): boolean {
        if (
            (!!this.dangerAboveThreshold || this.dangerAboveThreshold === 0) &&
            this.value >= this.dangerAboveThreshold
        ) {
            return true;
        }
        if (
            (!!this.dangerBelowThreshold || this.dangerBelowThreshold === 0) &&
            this.value <= this.dangerBelowThreshold
        ) {
            return true;
        }
        return this.danger;
    }
}
